import "./SuubeeWelcomeWizardComponent.scss";
import template from "./SuubeeWelcomeWizardComponent.html";
import {IComponentOptions, IHttpService, IOnDestroy, IScope, IWindowService} from "angular";
import LegalsJson from "../../legal/legal.json";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {Observable, ReplaySubject} from "rxjs";
import {LegalDocument, Legals, Section} from "../../legal/types/LegalDocument";
import {CurrentUser} from "../../../services/user/models/CurrentUser";
import {UserService} from "../../../services/user/UserService";
import {ThemeType} from "../../../types/UserConfig";
import {ThemingStore} from "../../../theming/store/ThemingStore";
import {IModalInstanceService} from "angular-ui-bootstrap";

export const SuubeeWelcomeWizard = "tfSuubeeWelcomeWizard";

export function suubeeWelcomeWizard(): IComponentOptions {
    return {
        template,
        bindings: {
            modalInstance: "<"
        },
        controllerAs: "ctrl",
        controller: SuubeeWelcomeWizardComponent
    };
}

export class SuubeeWelcomeWizardComponent implements IOnDestroy {

    /** @input */
    public readonly modalInstance: IModalInstanceService;

    public readonly title$: Observable<string>;
    public readonly legalIntro$: Observable<ReadonlyArray<string>>;
    public readonly legalSections$: Observable<ReadonlyArray<Section>>;

    public currentStepNumber = 0;
    public givenName: string | null = null;
    public surname: string | null = null;

    public priceInCents = false;
    public theme: ThemeType = "dark";

    private readonly legalDocs: LegalDocument = LegalsJson;

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService,
                       private readonly userService: UserService,
                       private readonly themingStore: ThemingStore,
                       private readonly $scope: IScope,
                       private readonly $http: IHttpService,
                       private readonly $window: IWindowService) {
        const content$ = this.buildLegalContent();
        this.title$ = content$.map((c) => c.title);
        this.legalIntro$ = content$.map((c) => c.introParagraphs).map((p) => p.map(this.htmlify));
        this.legalSections$ = content$.map((c) => c.sections)
                                      .map((sections) => {
                                          sections.forEach((s: any) => s.content = this.htmlify(s.content));
                                          return sections;
                                      });

        this.currentUserService
            .current
            .first()
            .subscribe((user) => this.setUpFormData(user));

        this.currentUserService
            .current
            .takeUntil(this.destroyed$)
            .map((user) => user.preferences)
            .map((prefs) => !!prefs.priceInCents)
            .subscribe((pic) => this.$scope.$applyAsync(() => this.priceInCents = pic));

        this.themingStore
            .currentTheme
            .takeUntil(this.destroyed$)
            .subscribe((theme) => this.$scope.$applyAsync(() => this.theme = theme));
    }

    private buildLegalContent(): Observable<Legals> {
        return this.currentUserService
                   .current
                   .map((user) => user.broker)
                   .distinctUntilChanged()
                   .map((broker) => this.legalDocs[broker] || LegalsJson.TF)
                   .publishReplay(1)
                   .refCount();
    }

    private htmlify(value: string) {
        const bold = /“(.*?)”/g;
        const italic = /‘(.*?)’/g;
        return value.replace(bold, "<b>“$1”</b>").replace(italic, "<i>‘$1’</i>");
    }

    private setUpFormData(user: CurrentUser) {
        if (user.profile.givenName != null) {
            this.givenName = user.profile.givenName;
            this.surname = user.profile.surname;
        } else {
            const fullName = user.shortName;
            const split = fullName.split(" ");
            const givenName = split[0];
            const surnameIndex = Math.max(1, split.length - 1);
            const surname = split[surnameIndex];
            this.givenName = givenName;
            this.surname = surname;
        }
    }

    public updateTheme(theme: ThemeType) {
        this.currentUserService.updatePreference("theme", theme);
    }

    public updatePriceInCents(priceInCents: boolean) {
        this.currentUserService.updatePreference("priceInCents", priceInCents);
    }

    public nextPage() {
        this.currentStepNumber = this.currentStepNumber + 1;
    }

    public previousPage() {
        this.currentStepNumber = this.currentStepNumber - 1;
    }

    public submitPersonal() {
        this.userService.updateFullName(this.givenName!, this.surname!);
        this.nextPage();
    }

    public acceptTerms() {
        this.$http.put("/tradefloor/api/user/legal/terms", null);
        this.$http.put("/tradefloor/api/user/onboard", null);
        this.modalInstance.close();
    }

    public async declineTerms() {
        const response = await this.$http.get("/tradefloor/api/logout");
        this.$window.location.href = response.headers("location") || "/";
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
